import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { jsPDF } from "jspdf";
import { useAuth } from "../context/AuthContext";
import { PROFESSIONAL_ROLES, SOCIAL_LIFE_ROLES } from "../lib/constants";
import GuidePro from "./GuidePro";
import { Button } from "../components/ui/button";
import {
  ShieldCheck, LayoutDashboard, CalendarDays, Users, TrendingUp, Droplets,
  GraduationCap, LayoutGrid, DoorOpen, ArrowRight, CheckCircle2, BookOpen, Sparkles, Download, Crown, ClipboardCheck,
} from "lucide-react";

const META = {
  roles: { icon: ShieldCheck, a: "#34d399", b: "#0d9488" },
  dashboard: { icon: LayoutDashboard, a: "#60a5fa", b: "#4f46e5" },
  agenda: { icon: CalendarDays, a: "#38bdf8", b: "#2563eb" },
  residents: { icon: Users, a: "#a78bfa", b: "#7c3aed" },
  pp: { icon: ClipboardCheck, a: "#5eead4", b: "#0d9488" },
  crm: { icon: TrendingUp, a: "#fbbf24", b: "#f97316" },
  ceo: { icon: Crown, a: "#fbbf24", b: "#eab308" },
  incontinence: { icon: Droplets, a: "#22d3ee", b: "#0891b2" },
  formation: { icon: GraduationCap, a: "#f472b6", b: "#db2777" },
  pilotage: { icon: LayoutGrid, a: "#2dd4bf", b: "#0d9488" },
  partenaires: { icon: DoorOpen, a: "#a3e635", b: "#10b981" },
};

export default function Guide() {
  const { t } = useTranslation();
  const { user } = useAuth();
  if (user && [...PROFESSIONAL_ROLES, ...SOCIAL_LIFE_ROLES].includes(user.role)) return <GuidePro />;
  const sections = t("guide.sections", { returnObjects: true }) || [];
  const isMainAdmin = user?.role === "admin";

  const downloadPDF = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const M = 42, W = pw - M * 2;
    let y = M;
    const nl = (h) => { y += h; if (y > ph - M) { doc.addPage(); y = M; } };
    const write = (text, size, style, color, gap = 6) => {
      doc.setFont("helvetica", style); doc.setFontSize(size); doc.setTextColor(...color);
      const lines = doc.splitTextToSize(String(text || ""), W);
      for (const ln of lines) { if (y > ph - M) { doc.addPage(); y = M; } doc.text(ln, M, y); y += size * 1.3; }
      y += gap;
    };
    // Cover
    doc.setFillColor(20, 12, 22); doc.rect(0, 0, pw, ph, "F");
    doc.setTextColor(52, 211, 153); doc.setFont("helvetica", "bold"); doc.setFontSize(30);
    doc.text("CarePilot", M, 120);
    doc.setTextColor(255, 255, 255); doc.setFontSize(22);
    doc.text(String(t("guide.title")), M, 160);
    doc.setTextColor(180, 160, 190); doc.setFont("helvetica", "normal"); doc.setFontSize(12);
    doc.splitTextToSize(String(t("guide.subtitle")), W).forEach((l, i) => doc.text(l, M, 190 + i * 18));
    doc.addPage(); y = M;
    (Array.isArray(sections) ? sections : []).forEach((s, idx) => {
      write(`${String(idx + 1).padStart(2, "0")}. ${s.title}`, 16, "bold", [124, 58, 237], 4);
      if (s.intro) write(s.intro, 11, "normal", [70, 70, 70], 8);
      if ((s.features || []).length) {
        write(t("guide.featuresLabel"), 11, "bold", [16, 120, 90], 3);
        (s.features || []).forEach((f) => write(`•  ${f}`, 10.5, "normal", [40, 40, 40], 2));
        nl(4);
      }
      if ((s.steps || []).length) {
        write(t("guide.stepsLabel"), 11, "bold", [16, 120, 90], 3);
        (s.steps || []).forEach((st, i) => write(`${i + 1}.  ${st}`, 10.5, "normal", [40, 40, 40], 2));
      }
      nl(14);
    });
    const total = doc.getNumberOfPages();
    for (let i = 1; i <= total; i++) { doc.setPage(i); doc.setFontSize(9); doc.setTextColor(150, 150, 150); doc.text(`CarePilot — ${i}/${total}`, pw - M, ph - 20, { align: "right" }); }
    doc.save(`CarePilot-Guide-${(t("guide.langCode", { defaultValue: "fr" }) || "fr")}.pdf`);
  };

  return (
    <div className="p-5 sm:p-8 max-w-[1100px] mx-auto space-y-8" data-testid="guide-page">
      {isMainAdmin && (
        <div className="flex justify-end -mb-4">
          <Button onClick={downloadPDF} className="gap-2 bg-emerald-600 hover:bg-emerald-500" data-testid="guide-download-pdf">
            <Download className="h-4 w-4" /> {t("guide.downloadPdf", { defaultValue: "Télécharger en PDF" })}
          </Button>
        </div>
      )}
      {/* Hero */}
      <div className="dash-card relative overflow-hidden p-7 sm:p-10">
        <span className="crm-aurora -left-16 -top-20 h-60 w-60" style={{ background: "radial-gradient(circle,rgba(16,185,129,0.5),transparent 70%)" }} />
        <span className="crm-aurora right-8 -top-24 h-56 w-56" style={{ background: "radial-gradient(circle,rgba(124,58,237,0.4),transparent 70%)", animationDelay: "-5s" }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold border border-emerald-400/20" style={{ color: "#34d399", background: "rgba(16,185,129,0.14)" }}>
            <BookOpen className="h-3.5 w-3.5" /> {t("guide.tag")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mt-3">
            <span style={{ background: "linear-gradient(120deg,#34d399,#22d3ee,#a78bfa)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>{t("guide.title")}</span>
          </h1>
          <p className="text-muted-foreground mt-3 text-sm sm:text-base max-w-2xl">{t("guide.subtitle")}</p>
          <div className="flex flex-wrap gap-2 mt-5">
            {Array.isArray(sections) && sections.map((s) => {
              const m = META[s.id] || META.dashboard;
              return (
                <a key={s.id} href={`#guide-${s.id}`} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] transition-colors" data-testid={`guide-jump-${s.id}`}>
                  <m.icon className="h-3.5 w-3.5" style={{ color: m.b }} /> {s.title}
                </a>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sections */}
      {Array.isArray(sections) && sections.map((s, idx) => {
        const m = META[s.id] || META.dashboard;
        return (
          <motion.section
            key={s.id} id={`guide-${s.id}`}
            initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.5 }}
            className="dash-card relative overflow-hidden p-6 sm:p-8 scroll-mt-24" data-testid={`guide-section-${s.id}`}
          >
            <span className="dash-glow -right-8 -top-10 h-36 w-36" style={{ background: `radial-gradient(circle, ${m.b}66, transparent 70%)` }} />
            <div className="relative flex items-start gap-4">
              <span className="dash-ico h-14 w-14 rounded-2xl flex items-center justify-center text-white shrink-0" style={{ background: `linear-gradient(135deg, ${m.a}, ${m.b})`, boxShadow: `0 14px 30px -12px ${m.b}` }}>
                <m.icon className="h-7 w-7" strokeWidth={1.6} />
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: m.b }}>{String(idx + 1).padStart(2, "0")}</span>
                  <h2 className="font-display text-2xl font-extrabold tracking-tight">{s.title}</h2>
                </div>
                <p className="text-muted-foreground mt-1 text-sm sm:text-base">{s.intro}</p>
              </div>
            </div>

            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {/* Features */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-foreground/60 mb-3">{t("guide.featuresLabel")}</p>
                <ul className="space-y-2">
                  {(s.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" style={{ color: m.b }} />
                      <span className="text-foreground/85">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Steps with arrow flow */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-foreground/60 mb-3">{t("guide.stepsLabel")}</p>
                <div className="space-y-0">
                  {(s.steps || []).map((st, i) => (
                    <div key={i} className="relative pl-9 pb-4 last:pb-0">
                      {i < (s.steps.length - 1) && <span className="absolute left-[13px] top-7 bottom-0 w-px" style={{ background: `${m.b}44` }} />}
                      <span className="absolute left-0 top-0 h-7 w-7 rounded-full flex items-center justify-center text-[12px] font-bold text-white" style={{ background: `linear-gradient(135deg, ${m.a}, ${m.b})` }}>{i + 1}</span>
                      <div className="flex items-center gap-1.5 pt-0.5">
                        <p className="text-sm text-foreground/85">{st}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-1 mt-1 text-xs font-semibold" style={{ color: m.b }}>
                  <span>{t("guide.stepLabel")} 1</span><ArrowRight className="h-3 w-3" /><span>{t("guide.stepLabel")} {(s.steps || []).length}</span><Sparkles className="h-3 w-3 ml-1" />
                </div>
              </div>
            </div>
          </motion.section>
        );
      })}
    </div>
  );
}
